import React, { useContext, useEffect, useState } from 'react';
import Layout from '~/components/Layout';
import { useTranslation } from 'react-i18next';
import { RouteTypes } from '~/routes/constants';
import { useParams } from 'react-router-dom';
import { Grid } from 'semantic-ui-react';
import dayjs from 'dayjs';
import { b64toBlob, handleErrors } from '~/utils/helpers';
import { ThemeContext } from 'styled-components';
import { FormTermsAndPrivacy, FormTermsWrapper, SchedulerRow, StyledGrid, StyledSegment } from './style'
import AuthService from '~/api/services/auth';
import { useAuth } from '~/routes/components/AuthProvider';
import TableComponent from '~/components/TableComponent';
import MyTimer from '~/components/Countdown';
import { SCHEDULED_STATUS } from '~/Constants';
import TermsModal from '~/components/TermsModal';
import Dialog from '~/components/Dialog';
import StockExChangeService from '~/api/services/stockExchange';
import { FixedPricePrefixType } from '~/api/services/dashboard';

const Show: React.FunctionComponent = () => {
  const [isBusy, setIsBusy] = useState(false)
  const { t } = useTranslation();
  const { id } = useParams()
  const [showData, setShowData] = useState(null)
  const { systemData: { languages, logged: { languageId }, amountUnit, amountVolume, amountPrice } } = useAuth()
  const [fileSource, setFileSource] = useState(null)
  const contextData = JSON.parse(localStorage.getItem('theme')) || useContext(ThemeContext)
  const [loadingTradingFile, setLoadingTradingFile] = useState(false)
  const [offerAmounts, setOffersAmounts] = useState(null)
  const [cashedData, setCashedData] = useState(contextData)
  const [acceptTermsModal, setAcceptTermsModal] = useState(false)
  const [farmerSettings, setFarmerSettings] = useState(null)
  const [settingsData, setSettingsData] = useState(null)
  const priceLabel = `(${amountPrice == 'e' ? '€' : '€ Ct'}/${amountVolume > 1 ? amountVolume : ''}${amountUnit})`
  const offersPreviewColumns = [
    {
      name: t('Delivery month'),
      key: 'date',
      type: 'dateRibbon',
      format: 'MMM YYYY'
    },
    {
      name: t('BLE price {{priceLabel}}', { priceLabel }),
      key: 'price',
      info: t('BLE price which was offered by the dairy within the stock exchange.'),
      type: 'no-unit-number'
    },
    {
      name: t('Max. Trading amount (in {{amountUnit}})', { amountUnit }),
      type: 'number-whole',
      key: 'maxAmount',
      info: t('Your maximum trading quantity is defined by your dairy. Your offered amount \n\
    must not exceed the maximum trading amount. In addition the confirmed BLE price volumes form the past \n\
    are being cosidered for the respective trading month.')
    },
    {
      name: t('Hedging amount (in {{amountUnit}})', { amountUnit }),
      key: 'offeredAmount',
      info: t('The amount you dairy has already confirme for the repsective trading month.'),
      type: 'number-whole'
    },
    {
      name: t('Status'),
      key: 'status',
      type: 'verified'
    }
  ]

  useEffect(() => {
    fetchShowFarmerData()
    if (cashedData && cashedData?.languageId !== languageId) {
      fetchAndCasheNewData()
    }
  }, [t])

  async function fetchAndCasheNewData() {
    setIsBusy(true)
    try {
      const language = languages.find(language => language?.id === languageId)
      const responseTermsData = await AuthService.getSettingsData({ languageCode: language.code || 'de' })
      setCashedData(responseTermsData.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function fetchShowFarmerData() {
    setIsBusy(true)
    try {
      const responseAccepted = await StockExChangeService.checkTradingRoomsAccept(id)
      const responseSettings = await StockExChangeService.getTradingSlotsFarmerSettings()
      setSettingsData(responseSettings.data)
      if (!responseAccepted.data) {
        if (responseSettings.data?.tradingRoom && responseSettings.data?.terms?.length > 0) {
          setAcceptTermsModal(true)
        } else {
          const response = await StockExChangeService.showTradingSlot(id)
          if (response.data.status === 'scheduled' || response.data.status === 'defined') {
            const responseFarmer = await StockExChangeService.farmerOfferAmountsSettings(id)
            setFarmerSettings(responseFarmer.data)
          }
          setShowData(response.data)
          const responseList = await StockExChangeService.farmerOfferAmountsList(id)
          setOffersAmounts(responseList.data)
        }
      }
      const response = await StockExChangeService.showTradingSlot(id)
      if (response.data.status === 'scheduled' || response.data.status === 'defined') {
        const responseFarmer = await StockExChangeService.farmerOfferAmountsSettings(id)
        setFarmerSettings(responseFarmer.data)
      }
      setShowData(response.data)
      const responseList = await StockExChangeService.farmerOfferAmountsList(id)
      setOffersAmounts(responseList.data)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderFileItems() {
    return settingsData?.terms?.map((term, index) => Boolean(term.visible) && renderFileType(term, index))
  }

  function renderShowData() {
    const dateFrom = showData?.dateFrom ? dayjs(showData?.dateFrom).format(('dddd, D MMMM YYYY HH:mm')) : '-'
    const timer = new Date(showData?.dateFrom)
    timer.setSeconds(timer.getSeconds() + 10) // set 10 seconds later so db can store in time status
    const dateTo = showData?.dateTo ? dayjs(showData?.dateTo).format(('dddd, D MMMM YYYY HH:mm')) : '-'
    const languageHasNoFiles = cashedData?.files?.filter(file => file?.languages?.filter(fileLang => !!fileLang.fileType).length !== 0).length === cashedData?.files?.length
    return <StyledSegment placeholder>
      <StyledGrid>
        <Grid.Row>
          <Grid.Column width={4}>{t('Status')}</Grid.Column>
          <Grid.Column className='column-value' width={8}>{showData?.statusLabel}</Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={4}>{t('Trading slot ID')}</Grid.Column>
          <Grid.Column className='column-value' width={8}>{showData?.uniqueId}</Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={4}>{t('Offer and binding period')}</Grid.Column>
          <Grid.Column className='column-value' width={8}>{t('From {{dateFrom}} to {{dateTo}}', { dateFrom, dateTo })}</Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={4}>{t('Info')}</Grid.Column>
          <Grid.Column className='column-value' width={8}>{showData?.description || t('Not defined')}</Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={4}>{t('Trading room documents')}</Grid.Column>
          <Grid.Column className='column-value' width={8}> <FormTermsWrapper>
            {languageHasNoFiles ? renderFileItems() : t('Not defined')}
          </FormTermsWrapper>
          </Grid.Column>
        </Grid.Row>
        {showData?.status === SCHEDULED_STATUS && <SchedulerRow>
          <Grid.Column width={4}>{t('Scheduled for')}</Grid.Column>
          <Grid.Column>
            <MyTimer
              onComplete={() => fetchShowFarmerData()}
              expiryTimestamp={timer} />
          </Grid.Column>
        </SchedulerRow>
        }
      </StyledGrid>
    </StyledSegment>
  }

  async function handleDownloadFile(fileName, id = null) {
    setLoadingTradingFile(id);
    try {
      const response = await StockExChangeService.openTradingRoomFile({
        fileName: fileName
      });
      const type = response.data.split(';')[0].split('data:')[1]
      const blob = b64toBlob(response.data, type);
      const blobUrl = URL.createObjectURL(blob);
      setFileSource(blobUrl);
    } catch (error) {
      handleErrors(error)
    }
    setLoadingTradingFile(false);
  }

  function renderFileType(item, index) {
    const addComma = index < settingsData?.terms?.length - 1
    return <FormTermsAndPrivacy
      style={{ color: `${cashedData?.firstColor}` }}
      key={item.id}
      target='blank'
      onClick={() => handleDownloadFile(item.filename, item.id)}
    >
      <StyledSegment loading={loadingTradingFile === item.id}>
        {`${item.title}${addComma ? ',' : ''}`}
      </StyledSegment>
    </FormTermsAndPrivacy>
  }

  function renderTable() {
    const columns = farmerSettings?.showPricesInLiveStatus ? offersPreviewColumns.filter(column => column.key !== 'price') : offersPreviewColumns
    return <TableComponent
      tableColumnHeaders={columns}
      rows={offerAmounts}
      total={offerAmounts.length}
      removePagination
    />
  }

  function renderModalPreviewFile() {
    return <Dialog
      open={Boolean(fileSource)}
      handleClose={() => setFileSource(null)}
      textContent={<iframe width='100%' height='100%' src={fileSource}></iframe>}
      textHeader={t('Preview')}
      textClose={t('Close')}
      type={'preview'}
      isLoading={isBusy}
    />
  }

  const uniqueId = showData?.uniqueId

  return <Layout
    segmented
    isBusy={isBusy}
    close={RouteTypes.STOCK_EXCHANGE}
    title={showData && t('Trading slot ID: {{uniqueId}}', { uniqueId })}>
    <TermsModal
      tradingSlotId={id}
      prefix={FixedPricePrefixType.StockExchange}
      openTermsModal={Boolean(acceptTermsModal)}
      settingsData={settingsData}
      handleClose={() => setAcceptTermsModal(null)}
    />
    {renderModalPreviewFile()}
    {showData && renderShowData()}
    {offerAmounts && renderTable()}
  </Layout>;
};

export default Show;
